import React, { useState, useEffect } from 'react';
import API from '../../services/api';

export default function SavedRepliesPage() {
  const [replies, setReplies]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [shortcut, setShortcut]   = useState('');
  const [title, setTitle]         = useState('');
  const [content, setContent]     = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => { loadReplies(); }, []);

  const loadReplies = async () => {
    try {
      const { data } = await API.get('/saved-replies');
      setReplies(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openNew = () => {
    setEditing(null);
    setShortcut(''); setTitle(''); setContent(''); setError('');
    setShowForm(true);
  };

  const openEdit = (reply) => {
    setEditing(reply);
    setShortcut(reply.shortcut);
    setTitle(reply.title);
    setContent(reply.content);
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!shortcut.trim() || !title.trim() || !content.trim()) {
      setError('All fields are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editing) {
        const { data } = await API.put(`/saved-replies/${editing._id}`, { shortcut, title, content });
        setReplies(prev => prev.map(r => r._id === editing._id ? data : r));
      } else {
        const { data } = await API.post('/saved-replies', { shortcut, title, content });
        setReplies(prev => [...prev, data]);
      }
      setShowForm(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this saved reply?')) return;
    try {
      await API.delete(`/saved-replies/${id}`);
      setReplies(prev => prev.filter(r => r._id !== id));
    } catch (err) { console.error(err); }
  };

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Saved Replies</h1>
          <p style={s.subtitle}>Type /shortcut in chat to use them instantly</p>
        </div>
        <button style={s.addBtn} onClick={openNew}>+ New Reply</button>
      </div>

      {/* Form */}
      {showForm && (
        <div style={s.formCard}>
          <div style={s.formTitle}>{editing ? 'Edit Reply' : 'New Saved Reply'}</div>
          <div style={s.formGrid}>
            <div style={s.field}>
              <label style={s.label}>SHORTCUT</label>
              <div style={s.inputWrap}>
                <span style={s.slashPrefix}>/</span>
                <input style={s.inputInner} placeholder="welcome" value={shortcut}
                  onChange={e => setShortcut(e.target.value.replace(/^\//, '').toLowerCase().replace(/\s/g, '-'))} />
              </div>
              <div style={s.hint}>Type this after / in chat to trigger</div>
            </div>
            <div style={s.field}>
              <label style={s.label}>TITLE</label>
              <input style={s.input} placeholder="Welcome Message" value={title}
                onChange={e => setTitle(e.target.value)} />
            </div>
          </div>
          <div style={s.field}>
            <label style={s.label}>REPLY CONTENT</label>
            <textarea style={{ ...s.input, height: '110px', resize: 'vertical' }}
              placeholder="Welcome to Blixo! How can I help you today? 😊"
              value={content} onChange={e => setContent(e.target.value)} />
          </div>
          {error && <div style={s.error}>{error}</div>}
          <div style={s.formActions}>
            <button style={s.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
            <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Update Reply' : 'Save Reply'}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div style={s.loading}>Loading...</div>
      ) : replies.length === 0 ? (
        <div style={s.empty}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
          <div style={{ color: 'rgba(255,255,255,.4)', fontSize: '15px', fontWeight: '600' }}>No saved replies yet</div>
          <div style={{ color: 'rgba(255,255,255,.25)', fontSize: '13px', marginTop: '6px' }}>Create one to speed up your support workflow</div>
        </div>
      ) : (
        <div style={s.list}>
          {replies.map(reply => (
            <div key={reply._id} style={s.replyCard}>
              <div style={s.replyTop}>
                <div style={s.replyLeft}>
                  <span style={s.shortcutBadge}>/{reply.shortcut}</span>
                  <span style={s.replyTitle}>{reply.title}</span>
                </div>
                <div style={s.replyActions}>
                  <button style={s.editBtn} onClick={() => openEdit(reply)}>✏️ Edit</button>
                  <button style={s.deleteBtn} onClick={() => handleDelete(reply._id)}>🗑️</button>
                </div>
              </div>
              <div style={s.replyContent}>{reply.content}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  container: { flex: 1, overflowY: 'auto', padding: '28px', fontFamily: "'Exo 2',sans-serif", background: '#080c14' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { margin: 0, fontSize: '24px', fontWeight: '700', color: '#e2e8f0', fontFamily: "'Rajdhani',sans-serif", letterSpacing: '1px' },
  subtitle: { margin: '4px 0 0', color: 'rgba(255,255,255,.3)', fontSize: '13px' },
  addBtn: { background: '#00ffb4', border: 'none', borderRadius: '10px', padding: '10px 20px', color: '#080c14', fontSize: '14px', fontWeight: '900', cursor: 'pointer', fontFamily: "'Rajdhani',sans-serif", letterSpacing: '1px', flexShrink: 0 },
  formCard: { background: '#0d1420', borderRadius: '16px', border: '1px solid rgba(0,255,180,.15)', padding: '24px', marginBottom: '24px' },
  formTitle: { color: '#00ffb4', fontSize: '14px', fontWeight: '700', letterSpacing: '2px', marginBottom: '20px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '7px', marginBottom: '16px' },
  label: { color: 'rgba(0,255,180,.7)', fontSize: '11px', fontWeight: '700', letterSpacing: '2px' },
  inputWrap: { display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(0,255,180,.15)', borderRadius: '10px', overflow: 'hidden' },
  slashPrefix: { color: '#00ffb4', fontSize: '16px', fontWeight: '700', padding: '0 10px', background: 'rgba(0,255,180,.06)', borderRight: '1px solid rgba(0,255,180,.1)', alignSelf: 'stretch', display: 'flex', alignItems: 'center' },
  inputInner: { flex: 1, background: 'transparent', border: 'none', padding: '11px 12px', color: '#fff', fontSize: '14px', fontFamily: "'Exo 2',sans-serif", outline: 'none' },
  input: { background: 'rgba(255,255,255,.04)', border: '1px solid rgba(0,255,180,.15)', borderRadius: '10px', padding: '11px 14px', color: '#fff', fontSize: '14px', fontFamily: "'Exo 2',sans-serif", outline: 'none', boxSizing: 'border-box', width: '100%' },
  hint: { color: 'rgba(255,255,255,.2)', fontSize: '11px' },
  error: { background: 'rgba(255,50,50,.08)', border: '1px solid rgba(255,50,50,.2)', color: '#ff6b6b', borderRadius: '9px', padding: '10px 14px', fontSize: '13px', marginBottom: '14px' },
  formActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
  cancelBtn: { background: 'transparent', border: '1px solid rgba(255,255,255,.1)', borderRadius: '9px', color: 'rgba(255,255,255,.4)', padding: '10px 20px', cursor: 'pointer', fontSize: '13px', fontFamily: "'Exo 2',sans-serif" },
  saveBtn: { background: '#00ffb4', border: 'none', borderRadius: '9px', color: '#080c14', padding: '10px 24px', cursor: 'pointer', fontSize: '14px', fontWeight: '900', fontFamily: "'Rajdhani',sans-serif", letterSpacing: '1px' },
  loading: { textAlign: 'center', color: 'rgba(255,255,255,.3)', padding: '60px' },
  empty: { textAlign: 'center', padding: '60px' },
  list: { display: 'flex', flexDirection: 'column', gap: '12px' },
  replyCard: { background: '#0d1420', borderRadius: '14px', border: '1px solid rgba(0,255,180,.08)', padding: '18px' },
  replyTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '10px' },
  replyLeft: { display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 },
  shortcutBadge: { background: 'rgba(0,255,180,.08)', border: '1px solid rgba(0,255,180,.2)', color: '#00ffb4', borderRadius: '7px', padding: '3px 10px', fontSize: '12px', fontWeight: '700', flexShrink: 0 },
  replyTitle: { color: '#e2e8f0', fontSize: '14px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  replyActions: { display: 'flex', gap: '6px', flexShrink: 0 },
  editBtn: { background: 'rgba(0,255,180,.06)', border: '1px solid rgba(0,255,180,.15)', borderRadius: '8px', color: '#00ffb4', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontFamily: "'Exo 2',sans-serif" },
  deleteBtn: { background: 'rgba(255,50,50,.07)', border: '1px solid rgba(255,50,50,.15)', borderRadius: '8px', color: '#ff6b6b', padding: '6px 10px', cursor: 'pointer', fontSize: '14px' },
  replyContent: { color: 'rgba(255,255,255,.4)', fontSize: '13px', lineHeight: '1.6', whiteSpace: 'pre-wrap' },
};
